import React, { useState, useEffect } from 'react';
import { createCertificateBody } from '@carboncare/schemas';
import { preflight } from '../services/api/schemaPreflight';
import PageHeader from '../components/common/PageHeader';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Tabs,
  Tab,
  ToggleButtonGroup,
  ToggleButton,
  TablePagination,
  Skeleton,
} from '@mui/material';
import {
  Add,
  Description,
  Delete,
  Download,
  Visibility,
} from '@mui/icons-material';
import { useAuthStore } from '../store/authStore';
import { AccountType } from '../types/auth';
import axios from 'axios';
import { CarbonCareDatePicker } from '../components/shared/CarbonCareDatePicker';
import LicenseeFilter from '../components/common/LicenseeFilter';

interface Certificate {
  id: number;
  certificate_number: string;
  company_id: number;
  company_name: string;
  sub_customer: string | null;
  sub_company_id: number | null;
  sub_company_name: string | null;
  generated_by: number;
  generated_by_email: string;
  date_from: string;
  date_to: string;
  standard: 'ISO' | 'EN';
  status: 'draft' | 'issued' | 'revoked';
  created_at: string;
  updated_at: string;
}

interface Company {
  id: number;
  name: string;
}

interface SubCustomer {
  id: number;
  name: string;
  code: string;
}

interface SubCompanyItem {
  id: number;
  name: string;
  code?: string;
  address_city?: string;
  address_country?: string;
}

interface PermissionCheck {
  hasPermission: boolean;
  reason: string;
}

interface CertFormData {
  companyId: string;
  subCustomer: string;
  subCompanyId: string;
  dateFrom: string;
  dateTo: string;
  standard: 'ISO' | 'EN';
}

type CertificateFor = 'subcompany' | 'subcustomer' | '';

const getStatusColor = (status: string) => {
  switch (status) {
    case 'issued':
      return 'success';
    case 'draft':
      return 'warning';
    case 'revoked':
      return 'error';
    default:
      return 'default';
  }
};

// Preview dialog body: spinner while loading, iframe when a URL is ready,
// otherwise an empty-state message. Extracted as a pure sub-component to keep
// the page component's cognitive complexity low.
const PreviewContent: React.FC<{ previewLoading: boolean; previewUrl: string | null }> = ({
  previewLoading,
  previewUrl,
}) => {
  if (previewLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (previewUrl) {
    return (
      <iframe
        src={previewUrl}
        style={{
          width: '100%',
          height: '100%',
          border: 'none',
          flex: 1,
        }}
        title="Certificate Preview"
      />
    );
  }
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', py: 8 }}>
      <Typography color="text.secondary">No preview available</Typography>
    </Box>
  );
};

type CertTab = 'licensee' | 'subcompany' | 'subcustomer';

const emptyStateMessage = (tab: CertTab): string => {
  if (tab === 'licensee') return 'No licensee certificates found. MasterAdmin needs to generate a licensee certificate first.';
  if (tab === 'subcompany') return 'No sub-company certificates found. Generate certificates for your sub-companies.';
  return 'No sub-customer certificates found. Generate certificates for your sub-customers.';
};

// Shared certificate table header, identical for the loading skeleton table
// and the populated data table.
const CertificateTableHead: React.FC<{ currentTab: CertTab }> = ({ currentTab }) => (
  <TableHead>
    <TableRow>
      <TableCell>Certificate #</TableCell>
      <TableCell>Licensee</TableCell>
      {currentTab === 'subcompany' && <TableCell>Sub-Company</TableCell>}
      {currentTab === 'subcustomer' && <TableCell>Sub-Customer</TableCell>}
      <TableCell>Period</TableCell>
      <TableCell>Standard</TableCell>
      <TableCell>Status</TableCell>
      <TableCell>Generated</TableCell>
      <TableCell align="right">Actions</TableCell>
    </TableRow>
  </TableHead>
);

interface CertificateTableContentProps {
  loading: boolean;
  certificates: Certificate[];
  currentTab: CertTab;
  totalCertificates: number;
  page: number;
  user: { accountType?: AccountType } | null | undefined;
  isLicenseeAdmin: boolean;
  onPreview: (id: number) => void;
  onDownload: (id: number, certificateNumber: string) => void;
  onDelete: (id: number) => void;
  onPageChange: (event: unknown, newPage: number) => void;
}

const canDeleteCertificate = (
  user: CertificateTableContentProps['user'],
  isLicenseeAdmin: boolean,
  currentTab: CertTab,
): boolean => {
  if (user?.accountType === AccountType.CarbonCareAdmin) return false;
  if (user?.accountType === AccountType.MasterAdmin) return true;
  return (
    isLicenseeAdmin &&
    (currentTab === 'subcustomer' || currentTab === 'subcompany') &&
    user?.accountType !== AccountType.SubCompanyAdmin
  );
};

// Card body of the certificates page: loading skeleton, empty state, or the
// populated, paginated table. Extracted from the page component to keep its
// cognitive complexity within the SonarJS threshold.
const CertificateTableContent: React.FC<CertificateTableContentProps> = ({
  loading,
  certificates,
  currentTab,
  totalCertificates,
  page,
  user,
  isLicenseeAdmin,
  onPreview,
  onDownload,
  onDelete,
  onPageChange,
}) => {
  if (loading) {
    return (
      <TableContainer component={Paper}>
        <Table>
          <CertificateTableHead currentTab={currentTab} />
          <TableBody>
            {Array.from({ length: 8 }, (_, i) => `cert-skeleton-${i}`).map((rowKey, idx) => (
              <TableRow key={rowKey}>
                <TableCell><Skeleton variant="text" width={120} /></TableCell>
                <TableCell><Skeleton variant="text" width={`${50 + ((idx * 9) % 30)}%`} /></TableCell>
                {(currentTab === 'subcompany' || currentTab === 'subcustomer') && (
                  <TableCell><Skeleton variant="text" width={120} /></TableCell>
                )}
                <TableCell><Skeleton variant="text" width={170} /></TableCell>
                <TableCell><Skeleton variant="rounded" width={64} height={22} sx={{ borderRadius: '12px' }} /></TableCell>
                <TableCell><Skeleton variant="rounded" width={70} height={22} sx={{ borderRadius: '12px' }} /></TableCell>
                <TableCell>
                  <Skeleton variant="text" width={90} />
                  <Skeleton variant="text" width={130} sx={{ fontSize: '0.75rem' }} />
                </TableCell>
                <TableCell align="right">
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                    <Skeleton variant="circular" width={28} height={28} />
                    <Skeleton variant="circular" width={28} height={28} />
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  if (certificates.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Description sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
        <Typography color="text.secondary">{emptyStateMessage(currentTab)}</Typography>
      </Box>
    );
  }

  return (
    <TableContainer component={Paper}>
      <Table>
        <CertificateTableHead currentTab={currentTab} />
        <TableBody>
          {certificates.map((cert) => (
            <TableRow key={cert.id} hover>
              <TableCell>
                <Typography variant="body2" fontWeight="medium">
                  {cert.certificate_number}
                </Typography>
              </TableCell>
              <TableCell>{cert.company_name}</TableCell>
              {currentTab === 'subcompany' && (
                <TableCell>{cert.sub_company_name || '-'}</TableCell>
              )}
              {currentTab === 'subcustomer' && (
                <TableCell>{cert.sub_customer || '-'}</TableCell>
              )}
              <TableCell>
                <Typography variant="body2">
                  {new Date(cert.date_from).toLocaleDateString()} -{' '}
                  {new Date(cert.date_to).toLocaleDateString()}
                </Typography>
              </TableCell>
              <TableCell>
                <Chip label={cert.standard} size="small" variant="outlined" />
              </TableCell>
              <TableCell>
                <Chip
                  label={cert.status.toUpperCase()}
                  size="small"
                  color={getStatusColor(cert.status) as any}
                />
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {new Date(cert.created_at).toLocaleDateString()}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  by {cert.generated_by_email}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => onPreview(cert.id)}
                  title="Preview certificate"
                >
                  <Visibility />
                </IconButton>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => onDownload(cert.id, cert.certificate_number)}
                  title="Download PDF"
                >
                  <Download />
                </IconButton>
                {canDeleteCertificate(user, isLicenseeAdmin, currentTab) && (
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => onDelete(cert.id)}
                    title="Delete certificate"
                  >
                    <Delete />
                  </IconButton>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <TablePagination
        component="div"
        count={totalCertificates}
        page={page}
        onPageChange={onPageChange}
        rowsPerPage={10}
        onRowsPerPageChange={() => {}}
        rowsPerPageOptions={[10]}
      />
    </TableContainer>
  );
};

const isGenerateDisabled = (
  creating: boolean,
  formData: CertFormData,
  isMasterAdmin: boolean,
  isLicenseeAdmin: boolean,
  certificateFor: CertificateFor,
): boolean =>
  creating ||
  !formData.dateFrom ||
  !formData.dateTo ||
  (isMasterAdmin && !formData.companyId);
  // TEMP DISABLED — sub-company / sub-customer certificate selection.
  // While hidden, licensee admins generate a licensee-level certificate (no target required).
  // Re-enable by changing the ';' above back to '||' and uncommenting these lines:
  // (isLicenseeAdmin && !certificateFor) ||
  // (isLicenseeAdmin && certificateFor === 'subcompany' && !formData.subCompanyId) ||
  // (isLicenseeAdmin && certificateFor === 'subcustomer' && !formData.subCustomer);

// Shared "Generate certificate for" sub-company/sub-customer toggle controls,
// used in both the MasterAdmin and LicenseeAdmin create-dialog branches.
const CertificateForToggle: React.FC<{
  certificateFor: CertificateFor;
  onSelect: (value: CertificateFor) => void;
}> = ({ certificateFor, onSelect }) => (
  <>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
      Generate certificate for:
    </Typography>
    <ToggleButtonGroup
      value={certificateFor}
      exclusive
      onChange={(_, value) => {
        if (value !== null) {
          onSelect(value);
        }
      }}
      fullWidth
      size="small"
    >
      <ToggleButton value="subcompany">Sub-Company</ToggleButton>
      <ToggleButton value="subcustomer">Sub-Customer</ToggleButton>
    </ToggleButtonGroup>
  </>
);

interface CreateCertificateFormBodyProps {
  creating: boolean;
  error: string | null;
  success: string | null;
  isMasterAdmin: boolean;
  isLicenseeAdmin: boolean;
  formData: CertFormData;
  setFormData: React.Dispatch<React.SetStateAction<CertFormData>>;
  certificateFor: CertificateFor;
  setCertificateFor: (value: CertificateFor) => void;
  companySubCompanies: SubCompanyItem[];
  companySubCustomers: SubCustomer[];
  licenseeSubCompanies: SubCompanyItem[];
  subCustomers: SubCustomer[];
  fetchingSubCompanies: boolean;
  fetchingSubCustomers: boolean;
  onLicenseeChange: (newCompanyId: string) => void;
}

// Body of the "Generate New Certificate" dialog: progress state, alerts, the
// MasterAdmin/LicenseeAdmin target selectors, date pickers and standard select.
// Extracted from the page component to keep its cognitive complexity in range.
const CreateCertificateFormBody: React.FC<CreateCertificateFormBodyProps> = ({
  creating,
  error,
  success,
  isMasterAdmin,
  isLicenseeAdmin,
  formData,
  setFormData,
  certificateFor,
  setCertificateFor,
  companySubCompanies,
  companySubCustomers,
  licenseeSubCompanies,
  subCustomers,
  fetchingSubCompanies,
  fetchingSubCustomers,
  onLicenseeChange,
}) => {
  if (creating) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={48} sx={{ mb: 2 }} />
        <Typography variant="body1" color="text.secondary">
          Generating certificate...
        </Typography>
      </Box>
    );
  }

  return (
    <>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mt: 1 }}>
        {isMasterAdmin && (
          <>
            <Grid item xs={12}>
              <LicenseeFilter
                value={formData.companyId ? Number(formData.companyId) : ''}
                onChange={(id) => onLicenseeChange(id === '' ? '' : String(id))}
                label="Licensee"
                required
                sx={{ width: '100%' }}
              />
            </Grid>

            {/* TEMP DISABLED — sub-company / sub-customer certificate selection (MasterAdmin).
                Temporarily hidden; licensee-level certificates are generated by default.
                Re-enable by removing the surrounding JSX comment markers here, in the
                LicenseeAdmin branch, and in isGenerateDisabled().
            <Grid item xs={12}>
              <CertificateForToggle certificateFor={certificateFor} onSelect={handleToggleSelect} />
              {!certificateFor && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Leave unselected to generate a licensee-level certificate
                </Typography>
              )}
            </Grid>

            {certificateFor === 'subcompany' && (
              <Grid item xs={12}>
                <FormControl fullWidth required disabled={!formData.companyId || fetchingSubCompanies}>
                  <InputLabel>Sub-Company</InputLabel>
                  <Select
                    value={formData.subCompanyId}
                    label="Sub-Company"
                    onChange={(e) => setFormData({ ...formData, subCompanyId: e.target.value })}
                    endAdornment={fetchingSubCompanies ? <CircularProgress size={20} sx={{ mr: 4 }} /> : null}
                  >
                    {companySubCompanies.map((sc) => (
                      <MenuItem key={sc.id} value={sc.id}>
                        {sc.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {!formData.companyId && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      Please select a licensee first to see available sub-companies
                    </Typography>
                  )}
                </FormControl>
              </Grid>
            )}

            {certificateFor === 'subcustomer' && (
              <Grid item xs={12}>
                <FormControl fullWidth required disabled={!formData.companyId || fetchingSubCustomers}>
                  <InputLabel>Sub-Customer</InputLabel>
                  <Select
                    value={formData.subCustomer}
                    label="Sub-Customer"
                    onChange={(e) => setFormData({ ...formData, subCustomer: e.target.value })}
                    endAdornment={fetchingSubCustomers ? <CircularProgress size={20} sx={{ mr: 4 }} /> : null}
                  >
                    {companySubCustomers.map((sc) => (
                      <MenuItem key={sc.id} value={sc.name}>
                        {sc.name} ({sc.code})
                      </MenuItem>
                    ))}
                  </Select>
                  {!formData.companyId && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      Please select a licensee first to see available sub-customers
                    </Typography>
                  )}
                </FormControl>
              </Grid>
            )}
            */}
          </>
        )}

        {isLicenseeAdmin && (
          <>
            {/* TEMP DISABLED — sub-company / sub-customer certificate selection (LicenseeAdmin).
                Temporarily hidden; licensee-level certificates are generated by default.
                Re-enable by removing the surrounding JSX comment markers (see MasterAdmin
                branch above and isGenerateDisabled()).
            <Grid item xs={12}>
              <CertificateForToggle certificateFor={certificateFor} onSelect={handleToggleSelect} />
            </Grid>

            {certificateFor === 'subcompany' && (
              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel>Sub-Company</InputLabel>
                  <Select
                    value={formData.subCompanyId}
                    label="Sub-Company"
                    onChange={(e) => setFormData({ ...formData, subCompanyId: e.target.value })}
                  >
                    {licenseeSubCompanies.map((sc) => (
                      <MenuItem key={sc.id} value={sc.id}>
                        {sc.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {certificateFor === 'subcustomer' && (
              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel>Sub-Customer</InputLabel>
                  <Select
                    value={formData.subCustomer}
                    label="Sub-Customer"
                    onChange={(e) => setFormData({ ...formData, subCustomer: e.target.value })}
                  >
                    {subCustomers.map((subCustomer) => (
                      <MenuItem key={subCustomer.id} value={subCustomer.code}>
                        {subCustomer.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            */}
          </>
        )}

        <Grid item xs={6}>
          <CarbonCareDatePicker
            fullWidth
            required
            label="From"
            placeholder="From Date"
            value={formData.dateFrom || null}
            onChange={(value) => setFormData({ ...formData, dateFrom: value })}
            maxDate={formData.dateTo ? new Date(formData.dateTo + 'T00:00:00') : undefined}
            portalId="root"
          />
        </Grid>

        <Grid item xs={6}>
          <CarbonCareDatePicker
            fullWidth
            required
            label="To"
            placeholder="To Date"
            value={formData.dateTo || null}
            onChange={(value) => setFormData({ ...formData, dateTo: value })}
            minDate={formData.dateFrom ? new Date(formData.dateFrom + 'T00:00:00') : undefined}
            portalId="root"
          />
        </Grid>

        <Grid item xs={12}>
          <FormControl fullWidth required>
            <InputLabel>Standard</InputLabel>
            <Select
              value={formData.standard}
              label="Standard"
              onChange={(e) => setFormData({ ...formData, standard: e.target.value as 'ISO' | 'EN' })}
            >
              <MenuItem value="ISO">ISO 14083:2023</MenuItem>
              {/* <MenuItem value="EN">EN 16258</MenuItem> */}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
    </>
  );
};

const CertificatesPage: React.FC = () => {
  const { user, token } = useAuthStore();
  const [certificates, setCertificates] = useState<Certificate[]>([]);
  const [subCustomers, setSubCustomers] = useState<SubCustomer[]>([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [companySubCustomers, setCompanySubCustomers] = useState<SubCustomer[]>([]);
  const [fetchingSubCustomers, setFetchingSubCustomers] = useState(false);
  const [creating, setCreating] = useState(false);
  const [permission, setPermission] = useState<PermissionCheck | null>(null);
  const [currentTab, setCurrentTab] = useState<'licensee' | 'subcompany' | 'subcustomer'>('licensee');

  // Sub-company state
  const [companySubCompanies, setCompanySubCompanies] = useState<SubCompanyItem[]>([]);
  const [fetchingSubCompanies, setFetchingSubCompanies] = useState(false);
  const [licenseeSubCompanies, setLicenseeSubCompanies] = useState<SubCompanyItem[]>([]);

  const [certificateFor, setCertificateFor] = useState<'subcompany' | 'subcustomer' | ''>('');
  const [page, setPage] = useState(0);
  const [totalCertificates, setTotalCertificates] = useState(0);

  const [formData, setFormData] = useState({
    companyId: '',
    subCustomer: '',
    subCompanyId: '',
    dateFrom: new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0], // Jan 1 of current year
    dateTo: new Date().toISOString().split('T')[0], // Today
    standard: 'ISO' as 'ISO' | 'EN',
  });

  const isMasterAdmin = user?.accountType === AccountType.MasterAdmin || user?.accountType === AccountType.CarbonCareAdmin;
  const isLicenseeAdmin = [AccountType.LicenseeAdmin, AccountType.SubCompanyAdmin].includes(user?.accountType!);

  useEffect(() => {
    fetchCertificates();
    checkPermission();
    if (isLicenseeAdmin) {
      fetchSubCustomers();
      fetchLicenseeSubCompanies();
    }

    if (user?.accountType === AccountType.SubCompanyAdmin || user?.accountType === AccountType.SubCustomerAdmin) {
      setCurrentTab('subcustomer');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCertificates = async (p = page, tab = currentTab) => {
    try {
      setLoading(true);
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/certificates`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { page: p + 1, limit: 10, type: tab },
      });
      const res = response.data;
      // Support both paginated { data, total } and legacy array response
      if (res.data && typeof res.total === 'number') {
        setCertificates(res.data);
        setTotalCertificates(res.total);
      } else {
        const arr = Array.isArray(res) ? res : [];
        setCertificates(arr);
        setTotalCertificates(arr.length);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch certificates');
    } finally {
      setLoading(false);
    }
  };


  const fetchSubCustomers = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/companies/${user?.companyId}/sub-customers`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setSubCustomers(Array.isArray(response.data?.data) ? response.data?.data : []);
    } catch (err: any) {
      console.error('Failed to fetch sub-customers:', err);
    }
  };

  const fetchLicenseeSubCompanies = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/sub-companies/company/${user?.companyId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const data = response.data.data || response.data || [];
      setLicenseeSubCompanies(Array.isArray(data) ? data : []);
    } catch (err: any) {
      console.error('Failed to fetch licensee sub-companies:', err);
    }
  };

  const fetchCompanySubCustomers = async (companyId: string) => {
    if (!companyId) {
      setCompanySubCustomers([]);
      return;
    }
    try {
      setFetchingSubCustomers(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/companies/${companyId}/sub-customers`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const data = response.data.data || response.data || [];
      setCompanySubCustomers(Array.isArray(data) ? data : []);
    } catch (err: any) {
      console.error('Failed to fetch company sub-customers:', err);
      setCompanySubCustomers([]);
    } finally {
      setFetchingSubCustomers(false);
    }
  };

  const fetchCompanySubCompanies = async (companyId: string) => {
    if (!companyId) {
      setCompanySubCompanies([]);
      return;
    }
    try {
      setFetchingSubCompanies(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/sub-companies/company/${companyId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const data = response.data.data || response.data || [];
      setCompanySubCompanies(Array.isArray(data) ? data : []);
    } catch (err: any) {
      console.error('Failed to fetch company sub-companies:', err);
      setCompanySubCompanies([]);
    } finally {
      setFetchingSubCompanies(false);
    }
  };

  const checkPermission = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/certificates/check-permission`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setPermission(response.data);
    } catch (err: any) {
      console.error('Failed to check permission:', err);
    }
  };

  const handleOpenCreateDialog = () => {
    setError(null);
    setSuccess(null);
    setCertificateFor('');
    if (isLicenseeAdmin && user?.companyId) {
      setFormData({ ...formData, companyId: user.companyId.toString(), subCompanyId: '', subCustomer: '' });
    } else {
      setCompanySubCustomers([]);
      setCompanySubCompanies([]);
    }
    setCreateDialogOpen(true);
  };

  const handleCloseCreateDialog = () => {
    setCreateDialogOpen(false);
    setCertificateFor('');
    setFormData({
      companyId: '',
      subCustomer: '',
      subCompanyId: '',
      dateFrom: new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0],
      dateTo: new Date().toISOString().split('T')[0],
      standard: 'ISO',
    });
  };

  const handleCreateCertificate = async () => {
    try {
      setError(null);
      setCreating(true);
      const body = {
        companyId: Number.parseInt(formData.companyId),
        subCustomer: formData.subCustomer || null,
        subCompanyId: formData.subCompanyId ? Number.parseInt(formData.subCompanyId) : null,
        dateFrom: formData.dateFrom,
        dateTo: formData.dateTo,
        standard: formData.standard,
      };
      preflight(createCertificateBody, body);
      await axios.post(
        `${process.env.REACT_APP_API_URL}/certificates`,
        body,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      handleCloseCreateDialog();
      fetchCertificates();
      setSuccess('Certificate created successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create certificate');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteCertificate = async (id: number) => {
    if (!globalThis.confirm('Are you sure you want to delete this certificate?')) {
      return;
    }

    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/certificates/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSuccess('Certificate deleted successfully');
      setTimeout(() => setSuccess(null), 3000);
      fetchCertificates();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete certificate');
    }
  };

  const handlePreviewCertificate = async (id: number) => {
    try {
      setPreviewLoading(true);
      setPreviewDialogOpen(true);

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/certificates/${id}/download`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
        }
      );

      // Create object URL for preview
      const url = globalThis.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      setPreviewUrl(url);
    } catch (err: any) {
      console.error('Failed to load certificate preview:', err);
      setError('Failed to load certificate preview');
      setPreviewDialogOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleClosePreview = () => {
    setPreviewDialogOpen(false);
    if (previewUrl) {
      globalThis.URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
  };

  const handleDownloadCertificate = async (id: number, certificateNumber: string) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/certificates/${id}/download`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
        }
      );

      // Create download link
      const url = globalThis.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `certificate-${certificateNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      globalThis.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('Failed to download certificate:', err);
      setError('Failed to download certificate');
    }
  };

  const handlePageChange = (_event: unknown, newPage: number) => {
    setPage(newPage);
    fetchCertificates(newPage);
  };

  const handleTabChange =(_e: React.SyntheticEvent, newValue: 'licensee' | 'subcompany' | 'subcustomer') => {
    setCurrentTab(newValue);
    setPage(0);
    fetchCertificates(0, newValue);
  };

  const handleLicenseeChange = (newCompanyId: string) => {
    setFormData({ ...formData, companyId: newCompanyId, subCustomer: '', subCompanyId: '' });
    setCertificateFor('');
    if (newCompanyId) {
      fetchCompanySubCustomers(newCompanyId);
      fetchCompanySubCompanies(newCompanyId);
    }
  };

  if (!isMasterAdmin && user?.accountType! < AccountType.SubCompanyAdmin) {
    return (
      <Box>
        <Alert severity="error">
          Access denied. Only MasterAdmin and LicenseeAdmin can manage certificates.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        description="View, download, and manage emissions certificates."
        actions={
          <>
            {(user?.accountType === AccountType.MasterAdmin || isLicenseeAdmin) && (
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleOpenCreateDialog}
                disabled={isLicenseeAdmin && !!permission && !permission.hasPermission}
                size="large"
              >
                Generate Certificate
              </Button>
            )}
          </>
        }
      />

      {isLicenseeAdmin && permission && !permission.hasPermission && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight="medium">Certificate Generation Locked</Typography>
          <Typography variant="body2">{permission.reason}</Typography>
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs
              value={currentTab}
              onChange={handleTabChange}
              aria-label="certificate tabs"
            >
              {user?.accountType !== AccountType.SubCompanyAdmin && user?.accountType !== AccountType.SubCustomerAdmin && (
                <Tab
                  label="Licensee Certificates"
                  value="licensee"
                />
              )}
              {/* Temporarily hidden — may be needed later
              <Tab
                label="Sub-Company Certificates"
                value="subcompany"
              />
              <Tab
                label="Sub-Customer Certificates"
                value="subcustomer"
              />
              */}
            </Tabs>
          </Box>

          <CertificateTableContent
            loading={loading}
            certificates={certificates}
            currentTab={currentTab}
            totalCertificates={totalCertificates}
            page={page}
            user={user}
            isLicenseeAdmin={isLicenseeAdmin}
            onPreview={handlePreviewCertificate}
            onDownload={handleDownloadCertificate}
            onDelete={handleDeleteCertificate}
            onPageChange={handlePageChange}
          />
        </CardContent>
      </Card>

      {/* Create Certificate Dialog */}
      <Dialog open={createDialogOpen} onClose={creating ? undefined : handleCloseCreateDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Generate New Certificate</DialogTitle>
        <DialogContent>
          <CreateCertificateFormBody
            creating={creating}
            error={error}
            success={success}
            isMasterAdmin={isMasterAdmin}
            isLicenseeAdmin={isLicenseeAdmin}
            formData={formData}
            setFormData={setFormData}
            certificateFor={certificateFor}
            setCertificateFor={setCertificateFor}
            companySubCompanies={companySubCompanies}
            companySubCustomers={companySubCustomers}
            licenseeSubCompanies={licenseeSubCompanies}
            subCustomers={subCustomers}
            fetchingSubCompanies={fetchingSubCompanies}
            fetchingSubCustomers={fetchingSubCustomers}
            onLicenseeChange={handleLicenseeChange}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreateDialog} variant="outlined">Cancel</Button>
          <Button
            onClick={handleCreateCertificate}
            variant="contained"
            disabled={isGenerateDisabled(creating, formData, isMasterAdmin, isLicenseeAdmin, certificateFor)}
            startIcon={creating ? <CircularProgress size={18} color="inherit" /> : null}
          >
            {creating ? 'Generating...' : 'Generate Certificate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Certificate Preview Dialog */}
      <Dialog
        open={previewDialogOpen}
        onClose={handleClosePreview}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            height: '90vh',
            maxHeight: '90vh',
          }
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Description color="primary" />
            <Typography variant="h6">Certificate Preview</Typography>
          </Box>
          <IconButton onClick={handleClosePreview} size="small">
            <Typography variant="button" sx={{ mr: 1 }}>Close</Typography>
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
          <PreviewContent previewLoading={previewLoading} previewUrl={previewUrl} />
        </DialogContent>
        <DialogActions sx={{
          borderTop: '1px solid',
          borderColor: 'divider',
          p: 2
        }}>
          <Button onClick={handleClosePreview} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CertificatesPage;
